import { RepartitionCard } from '@components/charts/analytics/RepartitionBar'
import { useAnalytics } from '@contexts/AnalyticsContext'
import {
  FetchMembershipAnalyticsGraphParams,
  MembershipAnalyticsGraphRepartitionItem,
} from '@models/membership/MembershipAnalyticsGraph'
import { fetchAnalyticsGraph } from '@services/AnalyticsGraph'
import InfoTip from '@components/charts/analytics/InfoTip'
import { Flex } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useRef, useState } from 'react'

/**
 * Plan repartition block (Figma 993:2070): stacked bar and inline legend of
 * each plan's share of the ACTIVE base today. Ignores the date range.
 */
const PlanRepartition = () => {
  const { selectedMembershipPrograms, isMembershipProgramReady } =
    useAnalytics()

  const [chartData, setChartData] = useState<
    MembershipAnalyticsGraphRepartitionItem[]
  >([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const latest = useRef(0)

  useEffect(() => {
    if (!isMembershipProgramReady) return

    const today = dayjs().format('YYYY-MM-DD')
    const params: FetchMembershipAnalyticsGraphParams = {
      entity: 'membership',
      kind: 'plan-repartition',
      startDate: today,
      endDate: today,
      programIds: selectedMembershipPrograms,
    }

    const requestId = ++latest.current
    setIsLoading(true)
    setError(undefined)

    fetchAnalyticsGraph<MembershipAnalyticsGraphRepartitionItem[]>(params)
      .then(({ data }) => {
        if (requestId !== latest.current) return
        setChartData(data || [])
        if (!data) setError('No data available.')
      })
      .catch((e) => {
        if (requestId !== latest.current) return
        console.error('Error fetching data:', e)
        setError('An error occurred while fetching data.')
      })
      .finally(() => {
        if (requestId === latest.current) setIsLoading(false)
      })
  }, [selectedMembershipPrograms, isMembershipProgramReady])

  return (
    <RepartitionCard
      title={
        <Flex align="center" gap={8}>
          <span>Plan repartition</span>
          <InfoTip title="Active subscriptions today, by plan." />
        </Flex>
      }
      data={chartData}
      isLoading={isLoading}
      error={error}
    />
  )
}

export default PlanRepartition
